<!-- AlertModal.vue -->
<script setup>
import { ref, watch, onMounted } from 'vue'

const isMounted = ref(false)

onMounted(() => {
  isMounted.value = true
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Alert'
  },
  message: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'danger', 'warning', 'info'].includes(value)
  },
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  showConfirm: {
    type: Boolean,
    default: true
  },
  showCancel: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['close', 'confirm'])

const isVisible = ref(props.show)

watch(() => props.show, (newVal) => {
  isVisible.value = newVal
})

const handleConfirm = () => {
  emit('confirm')
  emit('close')
}

const handleClose = () => {
  emit('close')
}

const getIcon = () => {
  switch (props.type) {
    case 'success':
      return 'check-circle'
    case 'danger':
      return 'alert-circle'
    case 'warning':
      return 'alert-triangle'
    default:
      return 'info-circle'
  }
}

const getColorClass = () => {
  return {
    'text-success': props.type === 'success',
    'text-danger': props.type === 'danger',
    'text-warning': props.type === 'warning',
    'text-info': props.type === 'info'
  }
}
</script>

<template>
  <Teleport to="body" :disabled="!isMounted">
    <div v-if="isVisible && isMounted" class="modal modal-blur d-block" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content">
          <div class="modal-status" :class="`bg-${type}`"></div>
          <div class="modal-body text-center py-4">
            <i :class="['icon', getColorClass(), `icon-${getIcon()}`]" style="font-size: 3rem;"></i>
            <h3 class="modal-title mt-2">{{ title }}</h3>
            <div class="text-muted">{{ message }}</div>
          </div>
          <div class="modal-footer">
            <div class="w-100">
              <div class="row">
                <div :class="showCancel ? 'col' : 'col-12'">
                  <button
                      v-if="showCancel"
                      class="btn w-100"
                      @click="handleClose"
                  >
                    {{ cancelText }}
                  </button>
                </div>
                <div :class="showCancel ? 'col' : 'col-12'">
                  <button
                      class="btn w-100"
                      :class="`btn-${type}`"
                      @click="handleConfirm"
                  >
                    {{ confirmText }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isVisible && isMounted" class="modal-backdrop fade show"></div>
  </Teleport>
</template>

<style scoped>
.modal-blur {
  backdrop-filter: blur(4px);
}

/* Ensure this modal is always above other UI (navbars, offcanvas, etc.) */
:deep(.modal.d-block) {
  z-index: 20000 !important;
}
:deep(.modal-backdrop) {
  z-index: 19990 !important;
}

.modal-status {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
}
</style>